/*!
 * Copyright © 2018-2022. Verizon Connect Ireland Limited. All rights reserved.
 */

import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { NgModule, Optional, SkipSelf } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MAT_LEGACY_DIALOG_DEFAULT_OPTIONS, MatLegacyDialogModule, MatLegacyDialogRef } from '@angular/material/legacy-dialog';
import { MAT_LEGACY_FORM_FIELD_DEFAULT_OPTIONS, MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { HammerModule } from '@angular/platform-browser';
import { SideDrawerComponent, SideDrawerModule } from '@fleetmatics/ui.base-library/side-drawer';
import { HelpPanelConnectorModule } from '@fleetmatics/ui.help-panel/connector';
import { InsightsPanelModule } from '@fleetmatics/ui.insights-panel';
import { FmNavbarModule } from '@fleetmatics/ui.navbar';
import { NotificationsConnectorModule } from '@fleetmatics/ui.notifications/connector';
import { FmCookieModule } from '@fleetmatics/ui.utilities';
import { EffectsModule } from '@ngrx/effects';
import { RouterState, RouterStateSerializer, StoreRouterConnectingModule } from '@ngrx/router-store';
import { INITIAL_STATE, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { environment } from '../../environments/environment';
import { ExternalModule } from '../external/external.module';
import { geofenceStateDevToolSanitizer } from '../geofence/store';
import { NavbarContainerComponent, VzcSideDrawerContainerComponent } from './containers';
import { throwIfAlreadyLoaded } from './module-import-guard';
import {
  AddressResolutionHttpService,
  AssetsHttpService,
  BalloonService,
  BrowserLocationService,
  CategoriesHttpService,
  CompoundIconRegistryService,
  CustomerMetadataHttpService,
  DeviceHealthCheckHttpService,
  DomHelperService,
  GarminStopsHttpService,
  GeofencesHttpService,
  GoogleMapsService,
  HotspotsHttpService,
  LayoutPreferencesService,
  MapIconRegistryService,
  ModalWindowService,
  NotificationHttpService,
  PlacesHttpService,
  PlanningSchedulingGarminHttpService,
  PlanningSchedulingHttpService,
  PlanningSchedulingWorkOrderHttpService,
  PlotFormattingService,
  PlotHttpService,
  RealTimeDataHttpService,
  RoadsideAssistanceHttpService,
  SchedulerHttpService,
  TokenService,
  UserDataHttpService,
  VehicleSortService,
  VideoCameraHttpService,
  VideoEventHttpService,
  WindowResizeService
} from './services';
import { MapPlotActionsDevToolsBlockList } from './store/actions';
import {
  AdvancedOptionsEffects,
  AssetsEffects,
  BalloonInfoEffects,
  CustomerMetadataEffects,
  DeviceHealthCheckEffects,
  DeviceHealthEffects,
  DriverMessageEffects,
  GarminStopHistoryEffects,
  HarshDrivingEventsEffects,
  InsightsPanelEffects,
  LayoutEffects,
  LayoutPreferencesEffects,
  MapsEffects,
  PlotsEffects,
  ReverseGeocodeEffects,
  RoadsideAssistanceEffects,
  SchedulerEffects,
  SendToGarminEffects,
  SidePanelEffects,
  UserMapSettingsEffects,
  VehicleEffects,
  VehicleListPanelEffects,
  WorkingTimeDirectiveEffects,
  WorkOrderEffects
} from './store/effects';
import { appReducers } from './store/reducers';
import { CustomRouterStateSerializer, initialAppState } from './store/state';

@NgModule({
  imports: [
    StoreModule.forRoot(appReducers, {
      runtimeChecks: {
        strictStateImmutability: false,
        strictActionImmutability: false
      }
    }),
    !environment.production
      ? StoreDevtoolsModule.instrument({
          stateSanitizer: geofenceStateDevToolSanitizer,
          actionsBlocklist: [...MapPlotActionsDevToolsBlockList]
        })
      : [],
    EffectsModule.forRoot([
      AdvancedOptionsEffects,
      AssetsEffects,
      BalloonInfoEffects,
      CustomerMetadataEffects,
      DriverMessageEffects,
      GarminStopHistoryEffects,
      HarshDrivingEventsEffects,
      InsightsPanelEffects,
      LayoutEffects,
      SidePanelEffects,
      LayoutPreferencesEffects,
      MapsEffects,
      PlotsEffects,
      ReverseGeocodeEffects,
      RoadsideAssistanceEffects,
      SchedulerEffects,
      SendToGarminEffects,
      UserMapSettingsEffects,
      VehicleEffects,
      VehicleListPanelEffects,
      WorkOrderEffects,
      WorkingTimeDirectiveEffects,
      DeviceHealthCheckEffects,
      DeviceHealthEffects
    ]),
    StoreRouterConnectingModule.forRoot({ routerState: RouterState.Minimal }),
    CommonModule,
    FmNavbarModule.forRoot(environment.config, environment.overrideBaseUrl),
    FmCookieModule.forRoot(),
    InsightsPanelModule.forRoot(environment.config),
    HttpClientModule,
    HammerModule,
    MatIconModule,
    HelpPanelConnectorModule.forRoot(() => import('../../lazy-components/help-panel-wrapper.component')),
    NotificationsConnectorModule.forRoot(environment.config, () =>
      import('./components/side-drawer/notifications-wrapper.module').then(m => m.NotificationsWrapperModule)
    ),
    SideDrawerModule,
    ExternalModule,
    MatLegacyDialogModule,
    MatLegacyFormFieldModule
  ],
  providers: [
    {
      provide: INITIAL_STATE,
      useValue: initialAppState
    },
    {
      provide: RouterStateSerializer,
      useClass: CustomRouterStateSerializer
    },
    // http services:
    AddressResolutionHttpService,
    AssetsHttpService,
    CategoriesHttpService,
    CustomerMetadataHttpService,
    GarminStopsHttpService,
    GeofencesHttpService,
    HotspotsHttpService,
    NotificationHttpService,
    PlacesHttpService,
    PlanningSchedulingGarminHttpService,
    PlanningSchedulingHttpService,
    PlanningSchedulingWorkOrderHttpService,
    PlotHttpService,
    RealTimeDataHttpService,
    RoadsideAssistanceHttpService,
    SchedulerHttpService,
    UserDataHttpService,
    VehicleSortService,
    VideoEventHttpService,
    VideoCameraHttpService,
    DeviceHealthCheckHttpService,
    // end http services
    // required for effects that are part of the core effects:
    BalloonService,
    GoogleMapsService,
    LayoutPreferencesService,
    ModalWindowService,
    // services that should be singleton because they have some shared state
    BrowserLocationService,
    CompoundIconRegistryService,
    MapIconRegistryService,
    WindowResizeService,
    // services that don't have a state and can potentially be removed from Core
    // but service will have multiple instances
    DomHelperService,
    PlotFormattingService,
    TokenService,
    {
      provide: MatLegacyDialogRef,
      useValue: {}
    },
    {
      provide: MAT_LEGACY_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        autoFocus: false
      }
    },
    { provide: MAT_LEGACY_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } }
  ],
  declarations: [NavbarContainerComponent, VzcSideDrawerContainerComponent],
  exports: [NavbarContainerComponent, SideDrawerComponent, VzcSideDrawerContainerComponent]
})
export class CoreModule {
  constructor(@Optional() @SkipSelf() parentModule: CoreModule) {
    throwIfAlreadyLoaded(parentModule, 'CoreModule');
  }
}
